import {
	Box,
	Chip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Theme,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useTranslation } from "react-i18next"

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
}

interface IstatusType {
	name: string
	bgColor: string
	color: string
}

const statusType: IstatusType[] = [
	{
		name: "SENT_OK",
		bgColor: "#98bd0a",
		color: "#333",
	},
	{
		name: "ERROR",
		bgColor: "#D32F2F",
		color: "#FFF",
	},
	{
		name: "DRAFT",
		bgColor: "#e8c600",
		color: "#222",
	},
	{
		name: "ACQUIRED",
		bgColor: "#007a4d",
		color: "#FFF",
	},
	{
		name: "REJECTED",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "CLOSED",
		bgColor: "#464646",
		color: "#FFF",
	},
	{
		name: "SENT_KO",
		bgColor: "#c82269",
		color: "#FFF",
	},
	{
		name: "CORPORATE_VERIFIED",
		bgColor: "#893de7",
		color: "#FFF",
	},
	{
		name: "BANK_CORPORATE_VERIFIED_1",
		bgColor: "#5258e4",
		color: "#FFF",
	},
	{
		name: "BANK_CORPORATE_VERIFIED_2",
		bgColor: "#5258e4",
		color: "#FFF",
	},
	{
		name: "BANK_CORPORATE_AUTHORIZER_VERIFIED",
		bgColor: "#6b48e5",
		color: "#FFF",
	},
	{
		name: "BANK_AUTHORIZER_VERIFIED",
		bgColor: "#0265dc",
		color: "#FFF",
	},
	{
		name: "BANK_AUTHORIZER_VERIFIED_2",
		bgColor: "#00719f",
		color: "#FFF",
	},
	{
		name: "BANK_AUTHORIZER_VERIFIED_1",
		bgColor: "#0168be",
		color: "#FFF",
	},
	{
		name: "BANK_VERIFIED",
		bgColor: "#27bb36",
		color: "#FFF",
	},
	{
		name: "FINAL_APPROVED",
		bgColor: "#007772",
		color: "#FFF",
	},
	{
		name: "NOT_ACQUIRED",
		bgColor: "#f68511",
		color: "#333",
	},
]

function getStyles(
	name: string,
	statusSelected: readonly IstatusType[],
	theme: Theme
) {
	return {
		fontWeight: statusSelected.includes(name)
			? theme.typography.fontWeightRegular
			: theme.typography.fontWeightMedium,
	}
}

interface IStatusMultiSelectProps {
	statusSelected: string[]
	setStatusSelected: (statusList: string[]) => void
}

const StatusMultiSelect = ({
	statusSelected,
	setStatusSelected,
}: IStatusMultiSelectProps) => {
	const theme = useTheme()
	const { t } = useTranslation()

	const handleChange = (event: SelectChangeEvent<typeof statusSelected>) => {
		const {
			target: { value },
		} = event

		setStatusSelected(
			// On autofill we get a stringified value.
			typeof value === "string" ? value.split(",") : value
		)
	}

	return (
		<FormControl sx={{ margin: 0, width: "100%" }} variant="standard">
			<InputLabel shrink={true} id="status-multiple-chip-label">
				{t("status", { ns: "common" })}
			</InputLabel>
			<Select
				labelId="status-multiple-chip-label"
				id="status-multiple-chip"
				variant="standard"
				multiple
				value={statusSelected}
				onChange={handleChange}
				// input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
				renderValue={(selectedArray: string[]) => {
					const displayArray = selectedArray.slice(0, 2)
					const remainingCount = selectedArray.length - displayArray.length
					return (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{displayArray.map((value) => (
								<Chip
									key={value}
									label={value}
									sx={{
										color:
											statusType?.find((status) => status?.name === value)
												?.color ?? "#333",
										backgroundColor:
											statusType?.find((status) => status?.name === value)
												?.bgColor ?? "#FFF",
									}}
								/>
							))}
							{remainingCount > 0 && <Chip label={`+${remainingCount}`} />}
						</Box>
					)
				}}
				MenuProps={MenuProps}
			>
				{statusType.map((currentStatus: IstatusType) => (
					<MenuItem
						key={currentStatus.name}
						value={currentStatus.name}
						style={getStyles(currentStatus.name, statusSelected, theme)}
					>
						{currentStatus.name}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	)
}

export default StatusMultiSelect
